import { useEffect, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { XIcon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import { Activity_Types_Enum_Enum } from '@graphql/__generated__/graphql';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useUpdateLeadAgents } from '@modules/_shared/hooks/useUpdateLeadAgents';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { GET_LEAD_AGENT_STATUS } from '@modules/lead-details/components/cards/LeadDetailsStatusCard';
import { GET_LEADS } from '@modules/leads/leadsQueries';
import { useLocale } from '@shared/hooks/locale';

const GET_LOST_STAGES = gql(/* GraphQL */ `
  query LeadLostDialogGetLostStages($pipeline_id: uuid!) {
    lead_stages(
      where: { pipeline_id: { _eq: $pipeline_id }, status: { _eq: lost } }
      order_by: { order_nr: asc }
    ) {
      id
      label
      label_en
      status
    }
  }
`);

const INSERT_LOST_ACTIVITY = gql(/* GraphQL */ `
  mutation LeadLostDialogInsertActivity($object: activities_insert_input!) {
    insert_activities_one(object: $object) {
      id
      lead_id
    }
  }
`);

type LeadLostDialogProps = {
  open: boolean;
  onClose: () => void;
  leadId?: string;
  pipelineId?: string | null;
  agentId?: string | null;
  currentStageId?: string | null;
  onStageUpdated?: (stageId: string) => void | Promise<void>;
};

export const LeadLostDialog = ({
  open,
  onClose,
  leadId,
  pipelineId,
  agentId,
  currentStageId,
  onStageUpdated,
}: LeadLostDialogProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const [selectionError, setSelectionError] = useState(false);
  const [errorLike, setErrorLike] = useState<ErrorLike | null>(null);

  const skeletonWidths = useMemo(() => {
    return Array.from({ length: 8 }, () => {
      const width = 10 + Math.random() * 38;
      return `${width.toFixed(0)}%`;
    });
  }, []);

  const {
    data,
    loading: stagesLoading,
    error: stagesError,
  } = useQuery(GET_LOST_STAGES, {
    variables: { pipeline_id: pipelineId ?? '' },
    skip: pipelineId == null,
  });

  useEffect(() => {
    if (stagesError != null) {
      setErrorLike(stagesError);
    }
  }, [stagesError]);

  const lostStages = useMemo(
    () => data?.lead_stages ?? [],
    [data?.lead_stages],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    if (lostStages.length === 0) {
      setSelectedStageId(null);
      return;
    }

    if (
      currentStageId &&
      lostStages.some(stage => stage.id === currentStageId)
    ) {
      setSelectedStageId(currentStageId);
      return;
    }

    setSelectedStageId(lostStages[0]?.id ?? null);
  }, [open, lostStages, currentStageId]);

  const [updateLeadAgents, { loading: updatingStage }] = useUpdateLeadAgents({
    onError: setErrorLike,
    refetchQueries: [GET_LEADS, GET_LEAD_AGENT_STATUS],
    awaitRefetchQueries: true,
  });

  const [insertLostActivity, { loading: insertingActivity }] = useMutation(
    INSERT_LOST_ACTIVITY,
    { onError: setErrorLike },
  );

  const handleInternalClose = () => {
    setSelectionError(false);
    onClose();
  };

  const handleConfirm = async () => {
    if (!selectedStageId) {
      setSelectionError(true);
      return;
    }

    const selectedStage = lostStages.find(
      stage => stage.id === selectedStageId,
    );

    if (!selectedStage) {
      setSelectedStageId(null);
      setSelectionError(true);
      return;
    }

    await insertLostActivity({
      variables: {
        object: {
          lead_id: leadId,
          assigned_to: agentId ?? undefined,
          note: selectedStage.label,
          activity_type: Activity_Types_Enum_Enum.Note,
          done: false,
          visibility: 'shared',
          ...(me?.is_admin ? { created_by: me?.id } : {}),
        },
      },
    });

    await updateLeadAgents(
      leadId ?? '',
      { stage_id: selectedStage.id },
      agentId ?? undefined,
    );

    if (onStageUpdated) {
      await onStageUpdated(selectedStage.id);
    }

    handleInternalClose();
  };

  const renderDialogBody = () => {
    if (stagesLoading) {
      return (
        <Stack
          sx={{
            gap: 1.5,
          }}
        >
          {skeletonWidths.map((width, index) => (
            <Skeleton key={index} variant="rounded" height={26} width={width} />
          ))}
        </Stack>
      );
    }

    if (lostStages.length === 0) {
      return (
        <Typography
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('No data')}
        </Typography>
      );
    }

    return (
      <FormControl error={selectionError && !selectedStageId} fullWidth>
        <RadioGroup
          value={selectedStageId ?? ''}
          onChange={event => {
            setSelectionError(false);
            setSelectedStageId(event.target.value);
          }}
        >
          {lostStages.map(stage => (
            <FormControlLabel
              key={stage.id}
              value={stage.id}
              control={<Radio />}
              label={stage.label ?? ''}
            />
          ))}
        </RadioGroup>
        {selectionError && !selectedStageId ? (
          <FormHelperText>{t('This field is required')}</FormHelperText>
        ) : null}
      </FormControl>
    );
  };

  const isSaveDisabled =
    lostStages.length === 0 ||
    !selectedStageId ||
    insertingActivity ||
    updatingStage;

  return (
    <>
      <SlideDialog
        open={open}
        onClose={handleInternalClose}
        title={t('Lead lost')}
        onSave={handleConfirm}
        loading={updatingStage || insertingActivity}
        saveDisabled={isSaveDisabled}
        saveText={t('Mark as lost')}
        cancelText={t('Cancel')}
        saveColor="error"
        saveIcon={<XIcon />}
      >
        <Stack
          sx={{
            gap: 2,
            p: 2,
          }}
        >
          <Typography variant="body1">{t('Why is this lead dead?')}</Typography>
          {renderDialogBody()}
        </Stack>
      </SlideDialog>
      <MutationErrorSnackbar
        error={errorLike}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};

export type { LeadLostDialogProps };
